import { Fragment, HTMLProps, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";

import { IReport } from "../../models/report/Report";
import Pagination from "../shared/Tables/Pagination";
import { DateTime } from "luxon";
import formatDateTime from "../../utils/formatDateTime";

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + 'p-3 cursor-pointer mr-0'}
      {...rest}
    />
  )
}

const colHelper = createColumnHelper<IReport>();

const cols = [
  // {
  //   id: 'select',
  //   header: ({ table }: any) => (
  //     <IndeterminateCheckbox
  //       {...{
  //         checked: table.getIsAllRowsSelected(),
  //         indeterminate: table.getIsSomeRowsSelected(),
  //         onChange: table.getToggleAllRowsSelectedHandler(),
  //       }}
  //     />
  //   ),
  //   cell: ({ row }: any) => (
  //     <div className="flex grow-0" onClick={(e) => e.stopPropagation()}>
  //       <IndeterminateCheckbox
  //         {...{
  //           id: row.original.id,
  //           checked: row.getIsSelected(),
  //           disabled: !row.getCanSelect(),
  //           indeterminate: row.getIsSomeSelected(),
  //           onChange: row.getToggleSelectedHandler(),
  //         }}
  //       />
  //     </div>
  //   ),
  // },
  colHelper.accessor("title", {
    header: "Report Name",
    sortingFn: "auto",
  }),
  colHelper.accessor("allowDates", {
    header: "Date Range",
    enableSorting: false,
    cell: (info) => (
      <span className={info.getValue() ? "text-green-600" : "text-gray-500"}>
        {info.getValue() ? "Yes" : "No"}
      </span>
    ),
  }),
  colHelper.accessor("lastRan", {
    header: "Last Run",
    sortingFn: "auto",
    cell: (info) => (
      <span>
        {info.getValue() 
          ? formatDateTime(DateTime.fromISO(info.getValue()!), DateTime.DATETIME_SHORT)
          : "Never"
        }
      </span>
    ),
  }),
  colHelper.accessor("created", {
    header: "Created",
    sortingFn: "auto",
    cell: (info) => formatDateTime(DateTime.fromISO(info.getValue()), DateTime.DATETIME_SHORT),
  }),
];

export default function ReportsTable({ data }: { data: IReport[] }) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: data,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    table.setPageSize(50);
  }, []);

  return (
    <div className="mt-4 bg-white overflow-auto">
      <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
              {headerGroup.headers.map((header, i) => (
                <th
                  key={header.id}
                  scope="col"
                  className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                >
                  {header.isPlaceholder
                    ? null
                    : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? 'cursor-pointer select-none'
                            : '',
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' ▲',
                          desc: ' ▼',
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, i) => {
            let rowCells = row.getVisibleCells();
            return (
              <Fragment key={i}>
                <tr
                  key={row.id}
                  onClick={() => navigate(`/reports/${row.original.id}`)}
                  className="hover:bg-gray-100 hover:cursor-pointer"
                >
                  {rowCells.map((cell) => (
                    <td key={cell.id} className="px-3 py-3 border text-sm">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <Pagination table={table} />
    </div>
  );
}
